import { zodResolver } from '@hookform/resolvers/zod'
import { FC, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import { validateCPF, validateDate } from 'src/utils/validations'
import { z } from 'zod'
import { useNotification } from '../../hooks/notification'
import { FormInput, FormSelectInput } from './components'

import { ArrowLeft } from 'lucide-react'
import appStore from 'src/assets/product-config/app-store.png'
import background from 'src/assets/product-config/background-signin.png'
import android from 'src/assets/product-config/google-play.png'
import { api } from 'src/services/api'
import './styles.css'

const FormSchema = z.object({
  email: z
    .string({
      required_error: 'Insira um e-mail',
    })
    .email('O e-mail fornecido não é válido.'),
  phone: z
    .string({
      required_error: 'Insira seu número',
    })
    .min(10, {
      message: 'O número de telefone deve ter pelo menos 10 caracteres.',
    })
    .max(15, {
      message: 'O número de telefone não pode ter mais de 15 caracteres.',
    }),

  name: z.string({
    required_error: 'Insira o seu Nome',
  }),
  document: z.string({
    required_error: 'Insira seu CPF ou CNPJ.',
  }),
  birthDate: z.string({
    required_error: 'Insira sua Data de nascimento.',
  }),
  gender: z.string({
    required_error: 'Insira seu Gênero.',
  }),
  maritalStatus: z.string({
    required_error: 'Insira seu Estado Cívil.',
  }),
  education: z.string({
    required_error: 'Insira seu Nível De Educação',
  }),
  monthlyIncome: z.string({
    required_error: 'Insira sua Renda mensal.',
  }),
})

type FormInputs = z.infer<typeof FormSchema>

export const InsertAccountData: FC = () => {
  const location = useLocation()

  const { accountType } = location.state

  const { setOpenNotification } = useNotification()

  const [loading, setLoading] = useState(false)

  const { control, handleSubmit, setError } = useForm<FormInputs>({
    resolver: zodResolver(FormSchema),
  })

  const navigate = useNavigate()

  const onSubmit = async (data: FormInputs) => {
    if (!data.name.includes(' ') || data.name.length < 5) {
      setError('name', { message: 'Preencha o nome completo.' })
      return
    }

    if (!validateCPF(data.document.replace(/[/,.,-\s]/g, ''))) {
      setError('document', { message: 'CPF Inválido.' })
      return
    }

    if (!validateDate(data.birthDate)) {
      setError('birthDate', {
        message: 'Preencha a data de emissão completa.',
      })
      return
    }

    setLoading(true)

    try {
      function formateCpf(cpf: string) {
        return cpf.replace(/\D/g, '')
      }

      const cpf = formateCpf(data.document)
      console.log(cpf)
      const response = await api.get(
        `graphic/verify/email/${data.email}/${cpf}`
      )
      console.log(response)
      if (response.status === 200) {
        localStorage.setItem('@yntech:signup-data-01', JSON.stringify(data))
        setLoading(false)
        navigate('/create-account-wallet/choose-plan', {
          state: { accountType },
        })
      }
    } catch (err) {
      setLoading(false)
      setOpenNotification({
        title: 'Alerta',
        notification: ' E-mail ou Cpf já esta cadastrado.',
        type: 'warning',
      })
    }
  }

  return (
    <div
      style={{
        backgroundImage: `url(${background})
		`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center center',
      }}
      className="w-full h-screen flex flex-col justify-around items-center gap-10"
    >
      <button
        onClick={() => navigate('/create-account-wallet/choose-account-type')}
        className="h-12 w-12 absolute left-10 top-10 flex items-center justify-center rounded-xl bg-zinc-950 shadow-sm"
      >
        <ArrowLeft size={22} color="white" />
      </button>
      <div className="flex flex-col absolute bottom-10 left-10">
        <span className="text-white text-xl font-bold">Baixe o nosso app,</span>
        <span className="text-white text-xl">
          disponível nas principais lojas
        </span>
        <div className="flex gap-2 mt-2">
          <img
            alt=""
            src={android}
            className="w-[150px] object-contain cursor-pointer"
          />
          <img
            alt=""
            src={appStore}
            className="w-[150px] object-contain cursor-pointer"
          />
        </div>
      </div>
      <div className="bg-black mr-10 overflow-y-auto self-end bg-opacity-40 rounded-3xl p-16 backdrop-filter backdrop-blur-md border border-gray-600 h-[95vh] max-w-[50%] flex flex-col justify-center items-center">
        <div className="w-full ">
          <form onSubmit={handleSubmit(onSubmit)}>
            {fields.map((item: any, index: number) => {
              return (
                <div key={item.id}>
                  {item.isDropdown && (
                    <Controller
                      name={item.id as any}
                      control={control}
                      render={({ field, fieldState }) => (
                        <>
                          <span className="font-bold text-white">
                            {item.placeholder}
                          </span>
                          <FormSelectInput
                            placeholder={item.placeholder}
                            options={item!.options!}
                            style={{
                              height: '5.5vh',
                            }}
                            onChanged={(e) => field.onChange(e)}
                            error={fieldState.error?.message}
                          />
                        </>
                      )}
                    />
                  )}

                  {!item.isDropdown && item.breakPoint === false && (
                    <Controller
                      name={item.id as any}
                      control={control}
                      render={({ field, fieldState }) => (
                        <>
                          <span className="font-bold text-white">
                            {item.placeholder}
                          </span>
                          <FormInput
                            onChangeText={(e) => field.onChange(e)}
                            style={{
                              height: '5.5vh',
                            }}
                            placeholder={item.placeholder}
                            required
                            {...field}
                            mask={item.mask}
                            errorMessage={fieldState.error?.message}
                          />
                        </>
                      )}
                    />
                  )}

                  {item.breakPoint && (
                    <div className="flex items-center gap-5 justify-between max-w-[50%]">
                      <Controller
                        name={item.id as any}
                        control={control}
                        render={({ field, fieldState }) => (
                          <div className="flex flex-col">
                            <span className="font-bold text-white">
                              {item.placeholder}
                            </span>
                            <FormInput
                              autoFocus
                              onChangeText={(e) => field.onChange(e)}
                              style={{
                                width: '20vw',
                                height: '5.5vh',
                              }}
                              placeholder={item.placeholder}
                              required
                              {...field}
                              mask={
                                field?.value?.length > 0 ? item.mask : undefined
                              }
                              errorMessage={fieldState.error?.message}
                            />
                          </div>
                        )}
                      />
                      <Controller
                        name={fields[index + 1].id as any}
                        control={control}
                        render={({ field, fieldState }) => (
                          <div className="flex flex-col">
                            <span className="font-bold text-white">
                              {item.placeholder}
                            </span>
                            <FormInput
                              onChangeText={(e) => field.onChange(e)}
                              style={{
                                width: '20vw',
                                height: '5.5vh',
                              }}
                              placeholder={fields[index + 1].placeholder}
                              required
                              {...field}
                              mask={fields[index + 1].mask}
                              errorMessage={fieldState.error?.message}
                            />
                          </div>
                        )}
                      />
                    </div>
                  )}
                </div>
              )
            })}

            <div className="flex flex-col w-full gap-2 items-center justify-between mt-4">
              <button
                className="bg-white h-16 w-full border border-zinc-300 rounded-md font-bold"
                onClick={handleSubmit(onSubmit)}
              >
                Próximo
              </button>
              <button
                className="bg-zinc-900 h-16 w-full border border-zinc-300 rounded-md font-bold text-white"
                onClick={() =>
                  navigate('/create-account-wallet/choose-account-type')
                }
              >
                Voltar
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

const fields = [
  {
    id: 'name',
    placeholder: 'Nome',
    breakPoint: false,
  },
  {
    id: 'email',
    placeholder: 'E-mail',
    breakPoint: false,
  },
  {
    id: 'phone',
    placeholder: 'Telefone',
    mask: '(99) 99999-9999',
    breakPoint: true,
  },
  {
    id: 'document',
    placeholder: 'CPF',
    mask: '999.999.999-99',
    breakPoint: undefined,
  },
  {
    id: 'maritalStatus',
    placeholder: 'Estado Civil',
    isDropdown: true,
    breakPoint: false,
    options: [
      'Solteiro(a)',
      'Casado(a)',
      'Separado(a) Legalmente',
      'Divorciado(a)',
      'União Estável',
      'Desimpedido(a)',
      'Viúvo(a)',
      'Outros',
    ],
  },
  {
    id: 'education',
    placeholder: 'Nível de educação',
    isDropdown: true,
    options: [
      'Analfabeto',
      'Ensino Fundamental - Anos Iniciais (1 a 4 anos)',
      'Ensino Fundamental - 5º ano',
      'Ensino Fundamental - 6º a 9º ano',
      'Ensino Fundamental Completo',
      'Ensino Médio Incompleto',
      'Ensino Médio Completo',
      'Ensino Superior Incompleto',
      'Ensino Superior Completo',
      'Pós-Graduação',
      'Mestrado',
      'Doutorado',
    ],
  },
  {
    id: 'monthlyIncome',
    placeholder: 'Renda Mensal',
    mask: 'currency',
    breakPoint: true,
  },
  {
    id: 'birthDate',
    placeholder: 'Data de nascimento ',
    mask: '99/99/9999',
    breakPoint: undefined,
  },
  {
    id: 'gender',
    placeholder: 'Gênero',
    isDropdown: true,
    options: ['Masculino', 'Feminino'],
    breakPoint: false,
  },
]
